import { Body1Strong, Button, Caption1, Textarea, makeStyles, tokens } from '@fluentui/react-components';
import { CheckmarkRegular } from '@fluentui/react-icons';

import {
  ASSESSMENT_VALUE_SOURCE,
  CONFIRMATION_STATE,
  TAssessmentFieldValue,
  TFieldValue,
  TTemplateFieldType,
} from '../../models';
import { OriginBadge } from './OriginBadge';
import { QuestionField } from './QuestionField';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  header: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  required: { color: tokens.colorPaletteRedForeground1 },
  muted: { color: tokens.colorNeutralForeground3 },
  note: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  confirmRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXXS,
  },
  secondary: { marginTop: tokens.spacingVerticalXS },
});

type TProps = {
  id: string;
  label: string;
  required: boolean;
  helpText?: string;
  controlType: TTemplateFieldType;
  options?: string[];
  value: TAssessmentFieldValue;
  disabled?: boolean;
  shownBecause?: string;
  confirmationNote?: string;
  allowsComment?: boolean;
  rationaleRequired?: boolean;
  onChange: (value: TFieldValue) => void;
  onComment?: (comment: string) => void;
  onRationale?: (rationale: string) => void;
  onConfirm?: () => void;
};

// One rendered field - a template custom field or an assessment question. Shows a
// subtle origin label, a confirm affordance for suggestions, and optional comment
// and rationale inputs. The control itself comes from the config-driven renderer.
export const AssessmentItemRow = ({
  id,
  label,
  required,
  helpText,
  controlType,
  options,
  value,
  disabled,
  shownBecause,
  confirmationNote,
  allowsComment,
  rationaleRequired,
  onChange,
  onComment,
  onRationale,
  onConfirm,
}: TProps) => {
  const classes = useStyles();
  const isUnconfirmedSuggestion =
    value.source === ASSESSMENT_VALUE_SOURCE.TEMPLATE_SUGGESTION &&
    value.confirmationState === CONFIRMATION_STATE.UNCONFIRMED;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Body1Strong id={id}>
          {label}
          {required ? <span className={classes.required}> *</span> : null}
        </Body1Strong>
        <OriginBadge value={value} />
      </div>
      {helpText ? <Caption1 className={classes.muted}>{helpText}</Caption1> : null}
      {shownBecause ? <Caption1 className={classes.note}>{shownBecause}</Caption1> : null}

      <QuestionField
        id={id}
        controlType={controlType}
        options={options}
        value={value.value}
        disabled={disabled}
        onChange={onChange}
      />

      {confirmationNote ? <Caption1 className={classes.muted}>{confirmationNote}</Caption1> : null}

      {isUnconfirmedSuggestion && !disabled && onConfirm ? (
        <div className={classes.confirmRow}>
          <Button appearance="subtle" size="small" icon={<CheckmarkRegular />} onClick={onConfirm}>
            Confirm suggestion
          </Button>
          <Caption1 className={classes.muted}>Confirm or change this suggested answer.</Caption1>
        </div>
      ) : null}

      {allowsComment ? (
        <Textarea
          className={classes.secondary}
          value={value.comment ?? ''}
          disabled={disabled}
          resize="vertical"
          placeholder="Add a comment"
          onChange={(_, data) => onComment?.(data.value)}
        />
      ) : null}

      {rationaleRequired ? (
        <Textarea
          className={classes.secondary}
          value={value.rationale ?? ''}
          disabled={disabled}
          resize="vertical"
          placeholder="Rationale (required)"
          onChange={(_, data) => onRationale?.(data.value)}
        />
      ) : null}
    </div>
  );
};
